import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";
import { useLang } from "../i18n/LanguageContext";
import { productCategories } from "../data/products";
import SEO from "../components/SEO";

const Products = () => {
  const { lang, t } = useLang();
  const [active, setActive] = useState(productCategories[0].key);
  const [query, setQuery] = useState("");

  const activeCat = productCategories.find((c) => c.key === active);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeCat.products;
    return activeCat.products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.formula || "").toLowerCase().includes(q)
    );
  }, [query, activeCat]);

  return (
    <>
      <SEO
        title="Product Catalog · Mining Chemicals & Reagents"
        description="Full catalog of mining reagents and consumables — grinding media, flotation collectors, leach reagents, flocculants and refining chemicals — supplied by Masika Chem SARL."
        path="/products"
      />

      {/* HEADER */}
      <section className="bg-white border-b border-neutral-200" data-testid="products-hero">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-20 pb-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C8102E]" />
                <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#C8102E]">
                  {t.products.overline}
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-neutral-900">
                {t.products.title}
              </h1>
            </div>
            <div className="md:col-span-5 flex items-end">
              <p className="text-lg text-neutral-600 leading-relaxed">{t.products.sub}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY TABS + SEARCH */}
      <section className="bg-[#F7F7F5] sticky top-[72px] z-30 border-b border-neutral-200" data-testid="products-tabs">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-4 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1">
            {productCategories.map((c) => (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                data-testid={`tab-${c.key}`}
                className={`whitespace-nowrap px-4 py-2 text-[12px] font-bold tracking-wide uppercase border transition-colors ${
                  active === c.key
                    ? "bg-[#171717] text-white border-[#171717]"
                    : "bg-white text-neutral-700 border-neutral-300 hover:border-[#C8102E] hover:text-[#C8102E]"
                }`}
              >
                {t.products.categories[c.key]}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-white border border-neutral-300 px-3 py-2 w-full lg:w-80">
            <MagnifyingGlass size={16} className="text-neutral-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="products-search"
              placeholder={lang === "fr" ? "Rechercher un produit..." : "Search products..."}
              className="bg-transparent outline-none text-sm w-full"
            />
          </div>
        </div>
      </section>

      {/* CATEGORY BANNER + TABLE */}
      <section className="bg-white" data-testid="products-table-section">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="relative aspect-[4/5] overflow-hidden bg-neutral-100">
                <img
                  src={activeCat.image}
                  alt={t.products.categories[active]}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#E85A1F]">
                    {t.products.overline}
                  </span>
                  <h2 className="mt-2 text-3xl font-extrabold text-white tracking-tight">
                    {t.products.categories[active]}
                  </h2>
                  <p className="mt-2 text-sm text-white/80">
                    {activeCat.products.length} products
                  </p>
                </div>
              </div>
            </div>

            <div className="md:col-span-8">
              <div className="border border-neutral-200">
                <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-neutral-50 border-b border-neutral-200 text-[11px] font-bold uppercase tracking-wider text-neutral-700">
                  <div className="col-span-5">{t.products.tableProduct}</div>
                  <div className="col-span-3">{t.products.tableFormula}</div>
                  <div className="col-span-3 hidden md:block">{t.products.tableApp}</div>
                  <div className="col-span-1 text-right">{t.products.tableCta}</div>
                </div>

                {filtered.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-neutral-500">
                    No products match your search.
                  </div>
                ) : (
                  filtered.map((p, i) => (
                    <div
                      key={i}
                      data-testid={`product-row-${i}`}
                      className="grid grid-cols-12 gap-4 px-5 py-5 border-b border-neutral-100 hover:bg-neutral-50 transition-colors items-center"
                    >
                      <div className="col-span-5">
                        <div className="font-semibold text-neutral-900">{p.name}</div>
                        <div className="md:hidden mt-1 text-xs text-neutral-500">
                          {lang === "fr" ? p.application_fr : p.application_en}
                        </div>
                      </div>
                      <div className="col-span-3 text-sm font-mono text-neutral-700">{p.formula}</div>
                      <div className="col-span-3 hidden md:block text-sm text-neutral-600">
                        {lang === "fr" ? p.application_fr : p.application_en}
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Link
                          to="/contact"
                          data-testid={`product-quote-${i}`}
                          className="inline-flex items-center gap-1 text-xs font-bold text-[#C8102E] hover:text-[#A00D25]"
                        >
                          {t.products.tableCta}
                          <ArrowRight size={12} weight="bold" />
                        </Link>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BLOCK */}
      <section className="bg-[#171717] text-white py-20" data-testid="products-cta">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <h3 className="md:col-span-8 text-3xl lg:text-4xl font-extrabold leading-[1.1] tracking-tight">
            Need a custom blend, datasheet or bulk shipment? Our team can help.
          </h3>
          <Link
            to="/contact"
            data-testid="products-cta-contact"
            className="md:col-span-4 md:justify-self-end inline-flex items-center gap-2 bg-[#C8102E] hover:bg-[#A00D25] text-white px-7 py-4 font-semibold"
          >
            {t.common.contactUs} <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default Products;
